import React, { useState, useMemo, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CTRMetrics = ({
  clicksData,
  impressionsData,
  geographicData,
  geographicImpressions,
  platformData,
  platformImpressions,
  sourceData,
  sourceImpressions,
}) => {
  const [viewType, setViewType] = useState("total"); // 'total', 'country', 'platform', 'source'
  const [isAggregate, setIsAggregate] = useState(true); // Whether to show aggregate data or specific date
  const [selectedDateIndex, setSelectedDateIndex] = useState(0); // Index of selected date
  const [showTopCount, setShowTopCount] = useState(10); // Number of top countries to show
  const [showAll, setShowAll] = useState(false); // Whether to show all countries
  const [minImpressions, setMinImpressions] = useState(0); // Minimum impressions filter for countries
  const [sourceMinImpressions, setSourceMinImpressions] = useState(0); // Minimum impressions filter for sources
  const [platformMinImpressions, setplatformMinImpressions] = useState(0); // Minimum impressions filter for platforms
  const [dateRange, setDateRange] = useState([null, null]); // Date range for source view
  const [startDate, endDate] = dateRange;

  // Debug log when data changes
  useEffect(() => {
    console.log("CTRMetrics data received:", {
      clicks: clicksData?.length || 0,
      impressions: impressionsData?.length || 0,
      geographic: geographicData?.length || 0,
      geographicImpressions: geographicImpressions?.length || 0,
      platform: platformData?.length || 0,
      platformImpressions: platformImpressions?.length || 0,
      source: sourceData?.length || 0,
      sourceImpressions: sourceImpressions?.length || 0,
    });
  }, [
    clicksData,
    impressionsData,
    geographicData,
    geographicImpressions,
    platformData,
    platformImpressions,
    sourceData,
    sourceImpressions,
  ]);

  // Format date for display
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  // Check if we have the necessary data
  const hasBaseData = useMemo(() => {
    return (
      clicksData &&
      clicksData.length > 0 &&
      impressionsData &&
      impressionsData.length > 0
    );
  }, [clicksData, impressionsData]);

  // Get all available dates from all data sources
  const availableDates = useMemo(() => {
    if (!hasBaseData) return [];

    const allDates = new Set([
      ...(clicksData || []).map((item) => item.date),
      ...(impressionsData || []).map((item) => item.date),
    ]);

    return [...allDates].sort((a, b) => new Date(a) - new Date(b));
  }, [clicksData, impressionsData, hasBaseData]);

  // Check if a date is within the valid date range
  const isDateAvailable = (date) => {
    return availableDates.some((dateStr) => {
      const availableDate = new Date(dateStr);
      return availableDate.toDateString() === date.toDateString();
    });
  };

  // Reset date range handler
  const resetDateRange = () => {
    setDateRange([null, null]);
  };

  // Process base data for total CTR over time
  const totalCTRData = useMemo(() => {
    if (!hasBaseData) return null;
    console.log("Processing totalCTRData with:", {
      clicks: clicksData?.length || 0,
      impressions: impressionsData?.length || 0,
    });

    // Get all unique dates from clicks and impressions
    const allDates = new Set([
      ...(clicksData || []).map((item) => item.date),
      ...(impressionsData || []).map((item) => item.date),
    ]);

    // Convert to array and sort chronologically
    const sortedDates = [...allDates].sort((a, b) => new Date(a) - new Date(b));
    console.log(`Found ${sortedDates.length} unique dates`);

    // Create a map for each date with metrics
    const dateMap = new Map();

    // Initialize with empty data for each date
    sortedDates.forEach((date) => {
      dateMap.set(date, {
        date,
        formattedDate: formatDate(date),
        clicks: 0,
        impressions: 0,
        ctr: 0,
      });
    });

    // Fill in the data
    if (clicksData) {
      clicksData.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).clicks = item.value;
        }
      });
    }

    if (impressionsData) {
      impressionsData.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).impressions = item.value;
        }
      });
    }

    // Calculate CTR
    dateMap.forEach((data) => {
      data.ctr =
        data.impressions > 0
          ? ((data.clicks / data.impressions) * 100).toFixed(2)
          : 0;
    });

    const processedData = [...dateMap.values()];
    console.log("Total CTR data processed:", processedData.length);

    return {
      dates: sortedDates,
      data: processedData,
    };
  }, [clicksData, impressionsData, hasBaseData]);

  // Process country CTR data - using country-specific impressions when available
  const countryCTRData = useMemo(() => {
    if (!hasBaseData || !geographicData || geographicData.length === 0)
      return null;

    // Check if we have country-specific impression data
    const hasCountryImpressions =
      geographicImpressions &&
      Array.isArray(geographicImpressions) &&
      geographicImpressions.length > 0;
    console.log(
      `Processing country CTR data with ${
        hasCountryImpressions ? "specific" : "estimated"
      } impressions`
    );

    // Filter for date range if selected
    let filteredGeographicData = geographicData;
    let filteredImpressions = impressionsData;
    let filteredClicksData = clicksData;
    let filteredGeographicImpressions = geographicImpressions;

    if (viewType === "country" && startDate && endDate) {
      filteredGeographicData = geographicData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      if (hasCountryImpressions) {
        filteredGeographicImpressions = geographicImpressions.filter((item) => {
          const itemDate = new Date(item.date);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      filteredImpressions = impressionsData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      filteredClicksData = clicksData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const processForDate = (dateIndex) => {
      const date = isAggregate ? null : availableDates[dateIndex];

      // For aggregate view or if specific date doesn't exist in geographic data
      let relevantClickData = isAggregate
        ? filteredGeographicData
        : filteredGeographicData.filter((item) => item.date === date);

      if (relevantClickData.length === 0) return null;

      // For specific date, we just need that date's data
      // For aggregate, we process all dates
      const latestDate = isAggregate
        ? filteredGeographicData[filteredGeographicData.length - 1].date
        : date;

      // Get sample data to extract all countries
      const sampleData = filteredGeographicData[0];

      // Extract countries excluding the 'date' field
      const countries = Object.keys(sampleData).filter((key) => key !== "date");

      // Process CTR for each country
      const countryData = countries
        .map((country) => {
          // Find corresponding clicks for this country
          let countryClicks = 0;
          let countryImpressions = 0;

          // Sum across relevant dates for this country
          relevantClickData.forEach((dateData) => {
            if (dateData[country]) {
              // Get the clicks for this country on this date
              const clicks = dateData[country];
              countryClicks += clicks;

              if (hasCountryImpressions) {
                // If we have country-specific impression data, use it
                const matchingImpressionData =
                  Array.isArray(filteredGeographicImpressions) &&
                  filteredGeographicImpressions.find(
                    (item) => item.date === dateData.date
                  );

                if (matchingImpressionData && matchingImpressionData[country]) {
                  countryImpressions += matchingImpressionData[country];
                }
              } else {
                // Otherwise use the estimation approach
                if (!isAggregate) {
                  // For specific date, calculate impressions directly using ratio from overall data
                  const dateClickData = filteredClicksData.find(
                    (item) => item.date === date
                  );
                  const dateImpressionData = filteredImpressions.find(
                    (item) => item.date === date
                  );

                  if (
                    dateClickData &&
                    dateImpressionData &&
                    dateClickData.value > 0
                  ) {
                    // Calculate country-specific impressions based on its proportion of total clicks
                    const countryClickProportion = clicks / dateClickData.value;
                    countryImpressions +=
                      dateImpressionData.value * countryClickProportion;
                  }
                } else {
                  // For aggregate view, calculate per-day and sum up
                  const itemDate = dateData.date;
                  const dayClickData = filteredClicksData.find(
                    (item) => item.date === itemDate
                  );
                  const dayImpressionData = filteredImpressions.find(
                    (item) => item.date === itemDate
                  );

                  if (
                    dayClickData &&
                    dayImpressionData &&
                    dayClickData.value > 0
                  ) {
                    const countryClickProportion = clicks / dayClickData.value;
                    countryImpressions +=
                      dayImpressionData.value * countryClickProportion;
                  }
                }
              }
            }
          });

          // Calculate CTR
          const ctr =
            countryImpressions > 0
              ? (countryClicks / countryImpressions) * 100
              : 0;

          return {
            name: country,
            clicks: countryClicks,
            impressions: countryImpressions,
            ctr: Math.floor(ctr * 100) / 100, // Format to 2 decimal places
            calculationMethod: hasCountryImpressions ? "actual" : "estimated",
          };
        })
        .filter(
          (item) =>
            item.clicks > 0 &&
            item.impressions > 0 &&
            item.impressions >= minImpressions
        );

      // Sort by CTR in descending order
      countryData.sort((a, b) => b.ctr - a.ctr);

      return {
        date: latestDate,
        formattedDate: formatDate(latestDate),
        data: countryData,
        isAggregate,
        useActualImpressions: hasCountryImpressions,
      };
    };

    return processForDate(selectedDateIndex);
  }, [
    geographicData,
    geographicImpressions,
    clicksData,
    impressionsData,
    hasBaseData,
    isAggregate,
    selectedDateIndex,
    availableDates,
    minImpressions,
    startDate,
    endDate,
    viewType,
  ]);

  // Process platform CTR data
  const platformCTRData = useMemo(() => {
    if (!hasBaseData || !platformData || platformData.length === 0) return null;

    // Check if we have platform-specific impression data
    const hasPlatformImpressions =
      platformImpressions &&
      Array.isArray(platformImpressions) &&
      platformImpressions.length > 0;
    console.log(
      `Processing platform CTR data with ${
        hasPlatformImpressions ? "specific" : "estimated"
      } impressions`
    );

    // Filter for date range if selected
    let filteredPlatformData = platformData;
    let filteredImpressions = impressionsData;
    let filteredClicksData = clicksData;
    let filteredPlatformImpressions = platformImpressions;

    if (viewType === "platform" && startDate && endDate) {
      filteredPlatformData = platformData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      if (hasPlatformImpressions) {
        filteredPlatformImpressions = platformImpressions.filter((item) => {
          const itemDate = new Date(item.date);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      filteredImpressions = impressionsData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      filteredClicksData = clicksData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const processForDate = (dateIndex) => {
      const date = isAggregate ? null : availableDates[dateIndex];

      // For aggregate view or if specific date doesn't exist in platform data
      let relevantClickData = isAggregate
        ? filteredPlatformData
        : filteredPlatformData.filter((item) => item.date === date);

      if (relevantClickData.length === 0) return null;

      // For specific date, we just need that date's data
      // For aggregate, we process all dates
      const latestDate = isAggregate
        ? filteredPlatformData[filteredPlatformData.length - 1].date
        : date;

      // Get sample data to extract all platforms
      const sampleData = filteredPlatformData[0];

      // Extract platforms excluding the 'date' field
      const platforms = Object.keys(sampleData).filter((key) => key !== "date");

      // Process CTR for each platform
      const platformDataResults = platforms
        .map((platform) => {
          // Find corresponding clicks for this platform
          let platformClicks = 0;
          let platformImpressionValue = 0;

          // Sum across relevant dates for this platform
          relevantClickData.forEach((dateData) => {
            if (dateData[platform]) {
              // Get the clicks for this platform on this date
              const clicks = dateData[platform];
              platformClicks += clicks;

              if (hasPlatformImpressions) {
                // If we have platform-specific impression data, use it
                const matchingImpressionData =
                  Array.isArray(filteredPlatformImpressions) &&
                  filteredPlatformImpressions.find(
                    (item) => item.date === dateData.date
                  );

                if (
                  matchingImpressionData &&
                  matchingImpressionData[platform]
                ) {
                  platformImpressionValue += matchingImpressionData[platform];
                }
              } else {
                // Otherwise estimate impressions based on overall ratio
                const dateImpressionData = filteredImpressions.find(
                  (item) => item.date === dateData.date
                );
                const dateClickData = filteredClicksData.find(
                  (item) => item.date === dateData.date
                );

                if (
                  dateImpressionData &&
                  dateClickData &&
                  dateClickData.value > 0
                ) {
                  const ratio = dateImpressionData.value / dateClickData.value;
                  platformImpressionValue += clicks * ratio;
                }
              }
            }
          });

          // Calculate CTR
          const ctr =
            platformImpressionValue > 0
              ? (platformClicks / platformImpressionValue) * 100
              : 0;

          return {
            name: platform,
            clicks: platformClicks,
            impressions: platformImpressionValue,
            ctr: parseFloat(ctr.toFixed(2)),
            calculationMethod: hasPlatformImpressions ? "actual" : "estimated",
          };
        })
        .filter(
          (item) =>
            item.clicks > 0 &&
            item.impressions > 0 &&
            item.impressions >= platformMinImpressions
        );

      // Sort by CTR in descending order
      platformDataResults.sort((a, b) => b.ctr - a.ctr);

      return {
        date: latestDate,
        formattedDate: formatDate(latestDate),
        data: platformDataResults,
        isAggregate,
        useActualImpressions: hasPlatformImpressions,
      };
    };

    return processForDate(selectedDateIndex);
  }, [
    platformData,
    platformImpressions,
    clicksData,
    impressionsData,
    hasBaseData,
    isAggregate,
    selectedDateIndex,
    availableDates,
    platformMinImpressions,
    startDate,
    endDate,
    viewType,
  ]);

  // Process source CTR data
  const sourceCTRData = useMemo(() => {
    if (!hasBaseData || !sourceData || sourceData.length === 0) return null;

    // Check if we have source-specific impression data
    const hasSourceImpressions =
      sourceImpressions &&
      Array.isArray(sourceImpressions) &&
      sourceImpressions.length > 0;
    console.log(
      `Processing source CTR data with ${
        hasSourceImpressions ? "specific" : "estimated"
      } impressions`
    );

    // Filter for date range if selected (for source view specifically)
    let filteredSourceData = sourceData;
    let filteredImpressions = impressionsData;
    let filteredClicksData = clicksData;
    let filteredSourceImpressions = sourceImpressions;

    if (viewType === "source" && startDate && endDate) {
      filteredSourceData = sourceData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      if (hasSourceImpressions) {
        filteredSourceImpressions = sourceImpressions.filter((item) => {
          const itemDate = new Date(item.date);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      filteredImpressions = impressionsData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });

      filteredClicksData = clicksData.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const processForDate = (dateIndex) => {
      const date = isAggregate ? null : availableDates[dateIndex];

      // For aggregate view or if specific date doesn't exist in source data
      let relevantClickData = isAggregate
        ? filteredSourceData
        : filteredSourceData.filter((item) => item.date === date);

      if (relevantClickData.length === 0) return null;

      // For specific date, we just need that date's data
      // For aggregate, we process all dates
      const latestDate = isAggregate
        ? filteredSourceData[filteredSourceData.length - 1].date
        : date;

      // Get sample data to extract all sources
      const sampleData = filteredSourceData[0];

      // Extract sources excluding the 'date' field
      const sources = Object.keys(sampleData).filter((key) => key !== "date");

      // Process CTR for each source
      const sourceDataResults = sources
        .map((source) => {
          // Find corresponding clicks for this source
          let sourceClicks = 0;
          let sourceImpressionValue = 0;

          // Sum across relevant dates for this source
          relevantClickData.forEach((dateData) => {
            if (dateData[source]) {
              // Get the clicks for this source on this date
              const clicks = dateData[source];
              sourceClicks += clicks;

              if (hasSourceImpressions) {
                // If we have source-specific impression data, use it
                const matchingImpressionData =
                  Array.isArray(filteredSourceImpressions) &&
                  filteredSourceImpressions.find(
                    (item) => item.date === dateData.date
                  );

                if (matchingImpressionData && matchingImpressionData[source]) {
                  sourceImpressionValue += matchingImpressionData[source];
                }
              } else {
                // Otherwise estimate impressions based on overall ratio
                const dateImpressionData = filteredImpressions.find(
                  (item) => item.date === dateData.date
                );
                const dateClickData = filteredClicksData.find(
                  (item) => item.date === dateData.date
                );

                if (
                  dateImpressionData &&
                  dateClickData &&
                  dateClickData.value > 0
                ) {
                  const ratio = dateImpressionData.value / dateClickData.value;
                  sourceImpressionValue += clicks * ratio;
                }
              }
            }
          });

          // Calculate CTR
          const ctr =
            sourceImpressionValue > 0
              ? (sourceClicks / sourceImpressionValue) * 100
              : 0;

          return {
            name: source,
            clicks: sourceClicks,
            impressions: sourceImpressionValue,
            ctr: parseFloat(ctr.toFixed(2)),
            calculationMethod: hasSourceImpressions ? "actual" : "estimated",
          };
        })
        .filter(
          (item) =>
            item.clicks > 0 &&
            item.impressions > 0 &&
            item.impressions >= sourceMinImpressions
        );

      // Sort by CTR in descending order
      sourceDataResults.sort((a, b) => b.ctr - a.ctr);

      return {
        date: latestDate,
        formattedDate: formatDate(latestDate),
        data: sourceDataResults,
        isAggregate,
        useActualImpressions: hasSourceImpressions,
      };
    };

    return processForDate(selectedDateIndex);
  }, [
    sourceData,
    sourceImpressions,
    clicksData,
    impressionsData,
    hasBaseData,
    isAggregate,
    selectedDateIndex,
    availableDates,
    sourceMinImpressions,
    startDate,
    endDate,
    viewType,
  ]);

  // Get stats for selected view
  const viewStats = useMemo(() => {
    if (viewType === "total" && totalCTRData) {
      const allCTRs = totalCTRData.data.map((item) => parseFloat(item.ctr));
      const avgCTR =
        allCTRs.reduce((sum, ctr) => sum + ctr, 0) / allCTRs.length;
      const maxCTR = Math.max(...allCTRs);
      const minCTR = Math.min(...allCTRs);

      return {
        average: avgCTR.toFixed(2),
        max: maxCTR.toFixed(2),
        min: minCTR.toFixed(2),
      };
    } else if (viewType === "country" && countryCTRData) {
      const allCTRs = countryCTRData.data.map((item) => item.ctr);
      const avgCTR =
        allCTRs.reduce((sum, ctr) => sum + ctr, 0) / allCTRs.length;

      return {
        average: avgCTR.toFixed(2),
        max: allCTRs[0].toFixed(2),
        bestPerformer: countryCTRData.data[0].name,
      };
    } else if (viewType === "platform" && platformCTRData) {
      const allCTRs = platformCTRData.data.map((item) => item.ctr);
      const avgCTR =
        allCTRs.reduce((sum, ctr) => sum + ctr, 0) / allCTRs.length;

      return {
        average: avgCTR.toFixed(2),
        max: allCTRs[0].toFixed(2),
        bestPerformer: platformCTRData.data[0].name,
      };
    } else if (viewType === "source" && sourceCTRData) {
      const allCTRs = sourceCTRData.data.map((item) => item.ctr);
      const avgCTR =
        allCTRs.reduce((sum, ctr) => sum + ctr, 0) / allCTRs.length;

      return {
        average: avgCTR.toFixed(2),
        max: allCTRs[0].toFixed(2),
        bestPerformer: sourceCTRData.data[0].name,
      };
    }

    return null;
  }, [viewType, totalCTRData, countryCTRData, platformCTRData, sourceCTRData]);

  // For trend visualization for the category views
  const categoryTrendData = useMemo(() => {
    if (!totalCTRData || !totalCTRData.data) return null;

    let trendData = {
      country: new Map(),
      platform: new Map(),
      source: new Map(),
    };

    // First, create the date structure for each
    totalCTRData.data.forEach((item) => {
      trendData.country.set(item.date, {
        date: item.date,
        formattedDate: item.formattedDate,
      });
      trendData.platform.set(item.date, {
        date: item.date,
        formattedDate: item.formattedDate,
      });
      trendData.source.set(item.date, {
        date: item.date,
        formattedDate: item.formattedDate,
      });
    });

    // Process geographic data by date
    if (geographicData) {
      geographicData.forEach((dateData) => {
        const date = dateData.date;
        if (!trendData.country.has(date)) return;

        const dayData = trendData.country.get(date);
        let totalClicks = 0;
        let totalImpressions = 0;

        Object.keys(dateData).forEach((key) => {
          if (key === "date") return;

          const clicks = dateData[key] || 0;
          totalClicks += clicks;

          // Estimate impressions based on overall ratio for this date
          const dateImpressionData = impressionsData.find(
            (item) => item.date === date
          );
          const dateClickData = clicksData.find((item) => item.date === date);

          if (dateImpressionData && dateClickData && dateClickData.value > 0) {
            const ratio = dateImpressionData.value / dateClickData.value;
            totalImpressions += clicks * ratio;
          }
        });

        dayData.clicks = totalClicks;
        dayData.impressions = totalImpressions;
        dayData.ctr =
          totalImpressions > 0
            ? ((totalClicks / totalImpressions) * 100).toFixed(2)
            : 0;
      });
    }

    // Process platform data by date
    if (platformData) {
      platformData.forEach((dateData) => {
        const date = dateData.date;
        if (!trendData.platform.has(date)) return;

        const dayData = trendData.platform.get(date);
        let totalClicks = 0;
        let totalImpressions = 0;

        Object.keys(dateData).forEach((key) => {
          if (key === "date") return;

          const clicks = dateData[key] || 0;
          totalClicks += clicks;

          // Estimate impressions based on overall ratio for this date
          const dateImpressionData = impressionsData.find(
            (item) => item.date === date
          );
          const dateClickData = clicksData.find((item) => item.date === date);

          if (dateImpressionData && dateClickData && dateClickData.value > 0) {
            const ratio = dateImpressionData.value / dateClickData.value;
            totalImpressions += clicks * ratio;
          }
        });

        dayData.clicks = totalClicks;
        dayData.impressions = totalImpressions;
        dayData.ctr =
          totalImpressions > 0
            ? ((totalClicks / totalImpressions) * 100).toFixed(2)
            : 0;
      });
    }

    // Process source data by date
    if (sourceData) {
      sourceData.forEach((dateData) => {
        const date = dateData.date;
        if (!trendData.source.has(date)) return;

        const dayData = trendData.source.get(date);
        let totalClicks = 0;
        let totalImpressions = 0;

        Object.keys(dateData).forEach((key) => {
          if (key === "date") return;

          const clicks = dateData[key] || 0;
          totalClicks += clicks;

          // Estimate impressions based on overall ratio for this date
          const dateImpressionData = impressionsData.find(
            (item) => item.date === date
          );
          const dateClickData = clicksData.find((item) => item.date === date);

          if (dateImpressionData && dateClickData && dateClickData.value > 0) {
            const ratio = dateImpressionData.value / dateClickData.value;
            totalImpressions += clicks * ratio;
          }
        });

        dayData.clicks = totalClicks;
        dayData.impressions = totalImpressions;
        dayData.ctr =
          totalImpressions > 0
            ? ((totalClicks / totalImpressions) * 100).toFixed(2)
            : 0;
      });
    }

    // Convert Maps to sorted arrays
    return {
      country: [...trendData.country.values()]
        .filter((item) => item.ctr !== undefined)
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
      platform: [...trendData.platform.values()]
        .filter((item) => item.ctr !== undefined)
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
      source: [...trendData.source.values()]
        .filter((item) => item.ctr !== undefined)
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
    };
  }, [
    totalCTRData,
    geographicData,
    platformData,
    sourceData,
    clicksData,
    impressionsData,
  ]);

  // Prepare chart data based on selected view type
  const chartData = useMemo(() => {
    if (viewType === "total" && totalCTRData) {
      return {
        labels: totalCTRData.data.map((item) => item.formattedDate),
        datasets: [
          {
            label: "Epic CTR (%)",
            data: totalCTRData.data.map((item) => parseFloat(item.ctr)),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.1)",
            tension: 0.4,
            fill: true,
          },
        ],
      };
    } else if (viewType === "country") {
      // Only return the bar chart data for countries - apply show options
      let countryData = countryCTRData?.data || [];
      if (countryCTRData && showTopCount > 0 && !showAll) {
        countryData = countryCTRData.data.slice(0, showTopCount);
      }

      const barChartData = countryCTRData
        ? {
            labels: countryData.map((item) => item.name),
            datasets: [
              {
                label: "CTR (%)",
                data: countryData.map((item) => item.ctr),
                backgroundColor: [
                  "rgba(54, 162, 235, 0.7)",
                  "rgba(255, 99, 132, 0.7)",
                  "rgba(255, 205, 86, 0.7)",
                  "rgba(75, 192, 192, 0.7)",
                  "rgba(153, 102, 255, 0.7)",
                  "rgba(255, 159, 64, 0.7)",
                  "rgba(201, 203, 207, 0.7)",
                  "rgba(54, 162, 235, 0.5)",
                  "rgba(255, 99, 132, 0.5)",
                  "rgba(255, 205, 86, 0.5)",
                ],
                borderColor: "rgba(255, 255, 255, 0.3)",
                borderWidth: 1,
              },
            ],
          }
        : null;

      return { barChartData };
    } else if (viewType === "platform") {
      // Only return the bar chart data for platforms
      const barChartData = platformCTRData
        ? {
            labels: platformCTRData.data.map((item) => item.name),
            datasets: [
              {
                label: "CTR (%)",
                data: platformCTRData.data.map((item) => item.ctr),
                backgroundColor: [
                  "rgba(54, 162, 235, 0.7)",
                  "rgba(255, 99, 132, 0.7)",
                  "rgba(255, 205, 86, 0.7)",
                  "rgba(75, 192, 192, 0.7)",
                  "rgba(153, 102, 255, 0.7)",
                ],
                borderColor: "rgba(255, 255, 255, 0.3)",
                borderWidth: 1,
              },
            ],
          }
        : null;

      return { barChartData };
    } else if (viewType === "source") {
      // Only return the bar chart data for sources
      const barChartData = sourceCTRData
        ? {
            labels: sourceCTRData.data.map((item) => item.name),
            datasets: [
              {
                label: "CTR (%)",
                data: sourceCTRData.data.map((item) => item.ctr),
                backgroundColor: [
                  "rgba(54, 162, 235, 0.7)",
                  "rgba(255, 99, 132, 0.7)",
                  "rgba(255, 205, 86, 0.7)",
                  "rgba(75, 192, 192, 0.7)",
                  "rgba(153, 102, 255, 0.7)",
                  "rgba(255, 159, 64, 0.7)",
                  "rgba(201, 203, 207, 0.7)",
                ],
                borderColor: "rgba(255, 255, 255, 0.3)",
                borderWidth: 1,
              },
            ],
          }
        : null;

      return { barChartData };
    }

    return null;
  }, [
    viewType,
    totalCTRData,
    countryCTRData,
    platformCTRData,
    sourceCTRData,
    showAll,
    showTopCount,
  ]);

  // Chart options based on selected view type
  const chartOptions = useMemo(() => {
    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            color: "#E5E7EB",
            font: {
              size: 12,
            },
            usePointStyle: true,
            padding: 15,
          },
        },
        tooltip: {
          backgroundColor: "#374151",
          titleColor: "#E5E7EB",
          bodyColor: "#E5E7EB",
          padding: 12,
          cornerRadius: 4,
          callbacks: {
            label: function (context) {
              const value = context.raw;
              return `CTR: ${value}%`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#E5E7EB",
          },
          grid: {
            color: "rgba(229, 231, 235, 0.1)",
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: "#E5E7EB",
            callback: function (value) {
              return value + "%";
            },
          },
          grid: {
            color: "rgba(229, 231, 235, 0.1)",
          },
          title: {
            display: true,
            text: "Click-Through Rate (%)",
            color: "#E5E7EB",
            font: {
              size: 12,
            },
          },
        },
      },
    };

    if (viewType === "total") {
      return {
        ...commonOptions,
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: "Epic CTR Over Time",
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
      };
    } else if (viewType === "country") {
      // Options for the bar chart
      const barOptions = {
        ...commonOptions,
        indexAxis: "y",
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: `CTR by Country - ${
              countryCTRData?.isAggregate
                ? "All Dates"
                : countryCTRData?.formattedDate || ""
            } ${
              !showAll && showTopCount > 0 ? `(Top ${showTopCount})` : "(All)"
            } ${
              minImpressions > 0
                ? `(Min ${minImpressions.toLocaleString()} impressions)`
                : ""
            }`,
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
      };

      return { barOptions };
    } else if (viewType === "platform") {
      // Options for the bar chart
      const barOptions = {
        ...commonOptions,
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: `CTR by Platform - ${
              platformCTRData?.isAggregate
                ? "All Dates"
                : platformCTRData?.formattedDate || ""
            } ${
              platformMinImpressions > 0
                ? `(Min ${platformMinImpressions.toLocaleString()} impressions)`
                : ""
            }`,
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
          legend: {
            display: false,
          },
        },
      };

      return { barOptions };
    } else if (viewType === "source") {
      // Options for the bar chart
      const barOptions = {
        ...commonOptions,
        indexAxis: "y",
        plugins: {
          ...commonOptions.plugins,
          title: {
            display: true,
            text: `CTR by Traffic Source - ${
              sourceCTRData?.isAggregate
                ? "All Dates"
                : sourceCTRData?.formattedDate || ""
            } ${
              sourceMinImpressions > 0
                ? `(Min ${sourceMinImpressions.toLocaleString()} impressions)`
                : ""
            }`,
            color: "#E5E7EB",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
      };

      return { barOptions };
    }

    return commonOptions;
  }, [
    viewType,
    countryCTRData,
    platformCTRData,
    sourceCTRData,
    showAll,
    showTopCount,
    minImpressions,
    sourceMinImpressions,
    platformMinImpressions,
  ]);

  if (!hasBaseData) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
          <h3 className="text-lg font-medium">
            Insufficient Data for CTR Analysis
          </h3>
          <p className="mt-2">
            Please upload both clicks and impressions data to view CTR metrics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">
            Click-Through Rate Analysis
          </h2>
          <p className="text-sm text-gray-400">
            Analyzing CTR across different dimensions
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setViewType("total")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewType === "total"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Epic CTR
          </button>
          <button
            onClick={() => setViewType("country")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewType === "country"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            } ${!geographicData ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!geographicData}
          >
            By Country
          </button>
          <button
            onClick={() => setViewType("platform")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewType === "platform"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            } ${!platformData ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!platformData}
          >
            By Platform
          </button>
          <button
            onClick={() => setViewType("source")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewType === "source"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            } ${!sourceData ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!sourceData}
          >
            By Source
          </button>
        </div>
      </div>

      {/* Date selector and aggregate toggle for category views */}
      {viewType !== "total" && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center bg-gray-800 rounded-md overflow-hidden">
            <button
              onClick={() => setIsAggregate(true)}
              className={`px-4 py-2 text-sm font-medium ${
                isAggregate
                  ? "bg-blue-600 text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              All Dates
            </button>
            <button
              onClick={() => setIsAggregate(false)}
              className={`px-4 py-2 text-sm font-medium ${
                !isAggregate
                  ? "bg-blue-600 text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              Specific Date
            </button>
          </div>

          {!isAggregate && availableDates.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="date-select" className="text-sm text-gray-300">
                Select Date:
              </label>
              <select
                id="date-select"
                value={selectedDateIndex}
                onChange={(e) => setSelectedDateIndex(Number(e.target.value))}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
              >
                {availableDates.map((date, index) => (
                  <option key={date} value={index}>
                    {formatDate(date)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Minimum impressions filter for country view */}
          {viewType === "country" && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="min-impressions"
                className="text-sm text-gray-300"
              >
                Min Impressions:
              </label>
              <input
                id="min-impressions"
                type="number"
                min="0"
                placeholder="0"
                value={minImpressions || ""}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? 0 : Number(e.target.value);
                  setMinImpressions(value);
                }}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
              />
            </div>
          )}

          {/* Show top countries selector for country view */}
          {viewType === "country" && (
            <div className="flex items-center gap-2 ml-auto">
              <label htmlFor="show-count" className="text-sm text-gray-300">
                Chart Display:
              </label>
              <select
                id="show-count"
                value={showAll ? "all" : showTopCount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "all") {
                    setShowAll(true);
                  } else {
                    setShowAll(false);
                    setShowTopCount(Number(value));
                  }
                }}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={15}>Top 15</option>
                <option value={20}>Top 20</option>
                <option value="all">Show All</option>
              </select>
            </div>
          )}
        </div>
      )}

      {viewType === "source" && (
        <div className="bg-gray-800 p-4 rounded-lg mb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Date Range
              </label>
              <div className="flex items-center gap-2">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setDateRange([date, endDate])}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="Start Date"
                  dateFormat="MM/dd/yyyy"
                  filterDate={isDateAvailable}
                  className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                  calendarClassName="bg-gray-800 border border-gray-700 text-white"
                  dayClassName={(date) =>
                    isDateAvailable(date)
                      ? "text-white hover:bg-blue-600"
                      : "text-gray-500 cursor-not-allowed"
                  }
                />
                <span className="text-gray-400">to</span>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setDateRange([startDate, date])}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText="End Date"
                  dateFormat="MM/dd/yyyy"
                  filterDate={isDateAvailable}
                  className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                  calendarClassName="bg-gray-800 border border-gray-700 text-white"
                  dayClassName={(date) =>
                    isDateAvailable(date)
                      ? "text-white hover:bg-blue-600"
                      : "text-gray-500 cursor-not-allowed"
                  }
                />
                {(startDate || endDate) && (
                  <button
                    onClick={resetDateRange}
                    className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md text-sm"
                    title="Reset date range"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label
                htmlFor="source-min-impressions"
                className="text-sm text-gray-300"
              >
                Min Impressions:
              </label>
              <input
                id="source-min-impressions"
                type="number"
                min="0"
                placeholder="0"
                value={sourceMinImpressions || ""}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? 0 : Number(e.target.value);
                  setSourceMinImpressions(value);
                }}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
              />
            </div>

            {startDate && endDate && (
              <div className="text-sm text-gray-400">
                Showing data from {startDate.toLocaleDateString()} to{" "}
                {endDate.toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {viewType === "platform" && (
        <div className="bg-gray-800 p-4 rounded-lg mb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Date Range
              </label>
              <div className="flex items-center gap-2">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setDateRange([date, endDate])}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="Start Date"
                  dateFormat="MM/dd/yyyy"
                  filterDate={isDateAvailable}
                  className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                  calendarClassName="bg-gray-800 border border-gray-700 text-white"
                  dayClassName={(date) =>
                    isDateAvailable(date)
                      ? "text-white hover:bg-blue-600"
                      : "text-gray-500 cursor-not-allowed"
                  }
                />
                <span className="text-gray-400">to</span>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setDateRange([startDate, date])}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText="End Date"
                  dateFormat="MM/dd/yyyy"
                  filterDate={isDateAvailable}
                  className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
                  calendarClassName="bg-gray-800 border border-gray-700 text-white"
                  dayClassName={(date) =>
                    isDateAvailable(date)
                      ? "text-white hover:bg-blue-600"
                      : "text-gray-500 cursor-not-allowed"
                  }
                />
                {(startDate || endDate) && (
                  <button
                    onClick={resetDateRange}
                    className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md text-sm"
                    title="Reset date range"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label
                htmlFor="platform-min-impressions"
                className="text-sm text-gray-300"
              >
                Min Impressions:
              </label>
              <input
                id="platform-min-impressions"
                type="number"
                min="0"
                placeholder="0"
                value={platformMinImpressions || ""}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? 0 : Number(e.target.value);
                  setplatformMinImpressions(value);
                }}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500 w-32"
              />
            </div>

            {startDate && endDate && (
              <div className="text-sm text-gray-400">
                Showing data from {startDate.toLocaleDateString()} to{" "}
                {endDate.toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {viewStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              Average CTR
            </h3>
            <p className="text-2xl font-bold text-gray-200">
              {viewStats.average}%
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              {viewType === "total" ? "Maximum CTR" : "Best CTR"}
            </h3>
            <p className="text-2xl font-bold text-gray-200">{viewStats.max}%</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 shadow-md">
            <h3 className="text-gray-400 text-sm font-medium mb-1">
              {viewType === "total" ? "Minimum CTR" : "Best Performer"}
            </h3>
            <p className="text-2xl font-bold text-gray-200">
              {viewType === "total"
                ? `${viewStats.min}%`
                : viewStats.bestPerformer}
            </p>
          </div>
        </div>
      )}

      <div className="bg-gray-800 p-6 rounded-lg">
        {viewType === "total" ? (
          // Single chart for total view
          chartData && chartOptions ? (
            <div className="h-96">
              <Line data={chartData} options={chartOptions} />
            </div>
          ) : (
            <div className="py-12 text-center text-gray-400">
              No data available for the total CTR view.
            </div>
          )
        ) : (
          // Single bar chart for category views
          <div>
            {chartData?.barChartData && chartOptions?.barOptions ? (
              <div className="h-96">
                <Bar
                  data={chartData.barChartData}
                  options={chartOptions.barOptions}
                />
              </div>
            ) : (
              <div className="py-6 text-center text-gray-400">
                No data available for this view.
              </div>
            )}
          </div>
        )}

        {/* Data table for category views */}
        {viewType !== "total" && (
          <div className="mt-6">
            {/* Indicator for impression data source */}
            {(() => {
              const categoryData =
                viewType === "country"
                  ? countryCTRData
                  : viewType === "platform"
                  ? platformCTRData
                  : sourceCTRData;

              if (categoryData && categoryData.useActualImpressions) {
                return (
                  <div className="mb-4 px-4 py-2 bg-green-900/30 rounded text-sm flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span className="text-white">
                      Using actual impression data for accurate CTR calculations
                    </span>
                  </div>
                );
              } else {
                return (
                  <div className="mb-4 px-4 py-2 bg-yellow-900/30 rounded text-sm flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      ></path>
                    </svg>
                    <span>
                      Using estimated impression data based on click proportions
                    </span>
                  </div>
                );
              }
            })()}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      {viewType === "country"
                        ? "Country"
                        : viewType === "platform"
                        ? "Platform"
                        : "Source"}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Clicks
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Impressions
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Calc CTR (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {(() => {
                    let data = [];
                    if (viewType === "country" && countryCTRData?.data) {
                      data = countryCTRData.data;
                    } else if (
                      viewType === "platform" &&
                      platformCTRData?.data
                    ) {
                      data = platformCTRData.data;
                    } else if (viewType === "source" && sourceCTRData?.data) {
                      data = sourceCTRData.data;
                    }
                    return data;
                  })().map((item, index) => (
                    <tr
                      key={item.name}
                      className={
                        index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {item.clicks.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {Math.round(item.impressions).toLocaleString()}
                        {item.calculationMethod === "estimated" && (
                          <span className="ml-1 text-xs text-yellow-400">
                            (est.)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {item.ctr.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CTRMetrics;
